import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePCAStore } from './stores/pcaStore';
import { Scene3D } from './components/Scene3D';
import { HyperslicerPanel } from './components/HyperslicerPanel';
import { ProfilePanel } from './components/ProfilePanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LandingPage } from './components/LandingPage';
import { QuizModal } from './components/QuizModal';
import { getColorForCluster, getClusterName } from './utils/colorMapping';
import { compareGuestsByDate } from './utils/guestDates';
import fireplaceLogo from './assets/fireplace-logo.png';

// Side panel icon component
function SidePanelIcon({ side, className = '' }: { side: 'left' | 'right'; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      {side === 'left' ? (
        <path d="M9 3v18" />
      ) : (
        <path d="M15 3v18" />
      )}
    </svg>
  );
}

// Decorative confetti/dot elements
function DecorativeElements() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Floating dots */}
      {Array.from({ length: 12 }).map((_, i) => (
        <motion.div
          key={`dot-${i}`}
          className="absolute rounded-full"
          style={{
            width: Math.random() * 8 + 4,
            height: Math.random() * 8 + 4,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            backgroundColor: i % 3 === 0 ? '#FF6B35' : i % 3 === 1 ? '#FFD23F' : '#FF8C42',
            opacity: 0.3 + Math.random() * 0.4,
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, Math.random() * 10 - 5, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 4 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

// Cluster Legend Component (clickable - highlights cluster in 3D space)
function ClusterLegend() {
  const { getUniqueClusters, selectedCluster, setSelectedCluster } = usePCAStore();
  const clusters = getUniqueClusters();

  if (clusters.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-1">
      {clusters.map(cluster => {
        const isSelected = selectedCluster === cluster;
        return (
          <button
            key={cluster}
            onClick={() => setSelectedCluster(isSelected ? null : cluster)}
            className={`flex items-center gap-1.5 px-1.5 py-1 rounded transition-colors text-left ${
              isSelected 
                ? 'bg-orange-primary/15' 
                : 'hover:bg-warm-surface-hover/50'
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-black/20"
              style={{ backgroundColor: getColorForCluster(cluster) }}
            />
            <span 
              className={`text-[11px] truncate ${
                isSelected ? 'text-text-primary font-medium' : 'text-text-secondary'
              }`} 
              title={getClusterName(cluster)}
            >
              {getClusterName(cluster)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Unified Guest Search & Compare Component
function GuestSearch({ onGuestSelect }: { onGuestSelect?: () => void }) {
  const { 
    pcaData, 
    setSelectedGuest, 
    selectedGuest, 
    getGuestCluster, 
    getMostDifferentGuest, 
    setMostDifferentComparison,
    setCompareComparison,
    comparisonFirstGuest,
    comparisonGuest,
    setComparisonGuest,
    userCoordinates,
    setQuizActive
  } = usePCAStore();
  
  // UI state
  const [query, setQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [editingPM, setEditingPM] = useState<'pm1' | 'pm2' | null>(null); // Which PM in comparison is being edited
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Determine current state
  const hasComparison = comparisonGuest !== null;
  const isCompareComplete = hasComparison && (comparisonFirstGuest !== null || selectedGuest !== null);
  const pm1 = comparisonFirstGuest || selectedGuest;
  const pm2 = comparisonGuest;

  // Get all guest names, sorted chronologically (latest first)
  const allGuests = useMemo(() => {
    if (!pcaData) return [];
    const guests = pcaData.guestCoordinates.map(g => g.guest);
    return guests.sort((a, b) => {
      const dateComparison = compareGuestsByDate(a, b);
      if (dateComparison !== 0) return dateComparison;
      return a.localeCompare(b);
    });
  }, [pcaData]);

  // Filter guests based on query, excluding already selected PM in compare mode or editing mode
  const filteredGuests = useMemo(() => {
    if (!isDropdownOpen) return [];
    
    let guests = allGuests;
    
    // When editing a comparison, exclude the OTHER PM
    if (editingPM === 'pm1' && pm2) {
      guests = guests.filter(g => g !== pm2);
    } else if (editingPM === 'pm2' && pm1) {
      guests = guests.filter(g => g !== pm1);
    } else if (isCompareMode && selectedGuest) {
      // In compare mode, exclude the first PM from the list
      guests = guests.filter(g => g !== selectedGuest);
    }
    
    if (!query.trim()) {
      return guests.slice(0, 20);
    }
    const lowerQuery = query.toLowerCase();
    return guests
      .filter(name => name.toLowerCase().includes(lowerQuery))
      .slice(0, 10);
  }, [query, allGuests, isDropdownOpen, isCompareMode, selectedGuest, editingPM, pm1, pm2]);

  // Handle PM selection
  const handleSelectPM = (guestName: string) => {
    if (editingPM === 'pm1') {
      // Editing first PM in comparison - update it while keeping PM2
      if (comparisonGuest) {
        setCompareComparison(guestName, comparisonGuest);
      } else {
        setSelectedGuest(guestName);
      }
      setEditingPM(null);
    } else if (editingPM === 'pm2') {
      // Editing second PM in comparison - update it while keeping PM1
      const firstPM = comparisonFirstGuest || selectedGuest;
      if (firstPM) {
        setCompareComparison(firstPM, guestName);
      }
      setEditingPM(null);
    } else if (isCompareMode && selectedGuest) {
      // Selecting second PM - complete comparison
      setCompareComparison(selectedGuest, guestName);
      setIsCompareMode(false);
    } else {
      // Selecting first PM
      setSelectedGuest(guestName);
    }
    
    setQuery('');
    setIsDropdownOpen(false);
    inputRef.current?.blur();
    onGuestSelect?.();
  };

  // Handle "Find Most Different" selection
  const handleFindMostDifferent = () => {
    if (!selectedGuest) return;
    const mostDifferent = getMostDifferentGuest(selectedGuest);
    if (mostDifferent) {
      setMostDifferentComparison(mostDifferent.name);
      setIsCompareMode(false);
      setIsDropdownOpen(false);
      onGuestSelect?.();
    }
  };

  // Enter compare mode
  const handleEnterCompareMode = () => {
    setIsCompareMode(true);
  };
  
  // Compare with yourself
  const handleCompareWithYourself = () => {
    if (!selectedGuest) return;
    
    if (userCoordinates) {
      // User has quiz data - set up comparison immediately
      // Set selected guest as first, user as comparison
      setCompareComparison(selectedGuest, '__USER__');
      onGuestSelect?.();
    } else {
      // User hasn't taken quiz - trigger quiz
      setQuizActive(true);
    }
    setIsDropdownOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Clear everything
  const handleClear = () => {
    setComparisonGuest(null);
    setSelectedGuest(null);
    setIsCompareMode(false);
    setEditingPM(null);
    setQuery('');
    setIsDropdownOpen(false);
  };

  // Clear comparison only (keep first PM)
  const handleClearComparison = () => {
    setComparisonGuest(null);
    setIsCompareMode(false);
    setEditingPM(null);
  };

  // Start editing a specific PM in comparison
  const handleEditPM = (which: 'pm1' | 'pm2') => {
    setEditingPM(which);
    setIsDropdownOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPM(null);
    setIsDropdownOpen(false);
    setQuery('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const showFindMostDifferentOption = (isCompareMode && selectedGuest) || (editingPM === 'pm2' && pm1);
    const totalItems = filteredGuests.length + (showFindMostDifferentOption ? 1 : 0);
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const showFindMostDifferent = (isCompareMode && selectedGuest) || (editingPM === 'pm2' && pm1);
      if (showFindMostDifferent && highlightedIndex === 0) {
        // Find most different
        const firstPM = editingPM === 'pm2' ? pm1 : selectedGuest;
        if (firstPM) {
          const mostDifferent = getMostDifferentGuest(firstPM);
          if (mostDifferent) {
            if (editingPM === 'pm2') {
              setCompareComparison(firstPM, mostDifferent.name);
              setEditingPM(null);
            } else {
              setMostDifferentComparison(mostDifferent.name);
              setIsCompareMode(false);
            }
            setIsDropdownOpen(false);
            onGuestSelect?.();
          }
        }
      } else {
        const adjustedIndex = showFindMostDifferent ? highlightedIndex - 1 : highlightedIndex;
        if (filteredGuests[adjustedIndex]) {
          handleSelectPM(filteredGuests[adjustedIndex]);
        }
      }
    } else if (e.key === 'Escape') {
      if (editingPM) {
        handleCancelEdit();
      } else if (isCompareMode) {
        setIsCompareMode(false);
        setIsDropdownOpen(false);
      } else {
        setIsDropdownOpen(false);
      }
      setQuery('');
      inputRef.current?.blur();
    }
  };

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredGuests, isCompareMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
        setEditingPM(null);
        if (isCompareMode && !comparisonGuest) {
          setIsCompareMode(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCompareMode, comparisonGuest]);

  // Exit compare mode when comparison is cleared externally
  useEffect(() => {
    if (!comparisonGuest && !isDropdownOpen) {
      setIsCompareMode(false);
    }
  }, [comparisonGuest, isDropdownOpen]);

  return (
    <div ref={containerRef} className="relative flex flex-col items-center">
      <AnimatePresence mode="wait">
        {/* STATE: Comparison Complete - Show result pill (or editing mode) */}
        {isCompareComplete && pm1 && pm2 && !isCompareMode ? (
          <motion.div
            key="comparison-result"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex items-stretch rounded-full overflow-hidden border border-warm-border/50 bg-warm-surface/95 backdrop-blur-md shadow-lg"
          >
            {/* PM 1 - clickable to edit, or show search when editing */}
            {editingPM === 'pm1' ? (
              <div className="relative flex-1 min-w-40 bg-orange-primary/10">
                <svg 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-primary/50"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onClick={() => setIsDropdownOpen(true)}
                  onFocus={() => setIsDropdownOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search PM..."
                  className="w-full bg-transparent text-text-primary pl-9 pr-3 py-2.5 text-sm focus:outline-none placeholder:text-orange-primary/50"
                  autoFocus
                />
              </div>
            ) : (
              <button
                onClick={() => handleEditPM('pm1')}
                className="px-4 py-2.5 flex items-center gap-2 bg-orange-primary/10 hover:bg-orange-primary/20 transition-colors cursor-pointer"
                title="Click to change"
              >
                <span 
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getColorForCluster(getGuestCluster(pm1)) || '#f97316' }}
                />
                <span className="text-sm font-medium text-text-primary truncate max-w-32">
                  {pm1 === '__USER__' ? 'You' : pm1}
                </span>
              </button>
            )}
            
            {/* VS divider */}
            <div className="flex items-center justify-center px-3 bg-warm-surface/50 text-text-secondary/50 text-xs font-medium">
              {comparisonFirstGuest ? 'vs' : '↔'}
            </div>
            
            {/* PM 2 - clickable to edit, or show search when editing */}
            {editingPM === 'pm2' ? (
              <div className="relative flex-1 min-w-40 bg-blue-500/10">
                <svg 
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400/50"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onClick={() => setIsDropdownOpen(true)}
                  onFocus={() => setIsDropdownOpen(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search PM..."
                  className="w-full bg-transparent text-text-primary pl-9 pr-3 py-2.5 text-sm focus:outline-none placeholder:text-blue-400/50"
                  autoFocus
                />
              </div>
            ) : (
              <button
                onClick={() => handleEditPM('pm2')}
                className="px-4 py-2.5 flex items-center gap-2 bg-blue-500/10 hover:bg-blue-500/20 transition-colors cursor-pointer"
                title="Click to change"
              >
                <span 
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getColorForCluster(getGuestCluster(pm2)) || '#3b82f6' }}
                />
                <span className="text-sm font-medium text-text-primary truncate max-w-32">
                  {pm2 === '__USER__' ? 'You' : pm2}
                </span>
                {!comparisonFirstGuest && (
                  <span className="text-[9px] text-red-400/70 font-medium uppercase tracking-wide">
                    most different
                  </span>
                )}
              </button>
            )}
            
            {/* Clear/Cancel button */}
            <button
              onClick={editingPM ? handleCancelEdit : handleClear}
              className="px-3 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-warm-surface-hover/50 transition-colors"
              title={editingPM ? "Cancel editing" : "Clear comparison"}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </motion.div>
        ) : isCompareMode && selectedGuest ? (
          /* STATE: Compare Mode - PM1 selected, searching for PM2 */
          <motion.div
            key="compare-mode"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex items-stretch rounded-full overflow-hidden border border-warm-border/50 bg-warm-surface/95 backdrop-blur-md shadow-lg"
          >
            {/* PM 1 - fixed */}
            <div className="px-4 py-2.5 flex items-center gap-2 bg-orange-primary/10 border-r border-warm-border/30">
              <span 
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: getColorForCluster(getGuestCluster(selectedGuest)) || '#f97316' }}
              />
              <span className="text-sm font-medium text-text-primary truncate max-w-28">
                {selectedGuest}
              </span>
            </div>
            
            {/* VS divider */}
            <div className="flex items-center justify-center px-2 bg-warm-surface/30 text-text-secondary/40 text-xs font-medium">
              vs
            </div>
            
            {/* PM 2 search input */}
            <div className="relative flex-1 min-w-48">
              <svg 
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary/50"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onClick={() => setIsDropdownOpen(true)}
                onFocus={() => setIsDropdownOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder="Search second PM..."
                className="w-full bg-transparent text-text-primary pl-9 pr-3 py-2.5 text-sm focus:outline-none placeholder:text-text-secondary/50"
                autoFocus
              />
            </div>
            
            {/* Cancel button */}
            <button
              onClick={handleClearComparison}
              className="px-3 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-warm-surface-hover/50 transition-colors"
              title="Cancel comparison"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </motion.div>
        ) : selectedGuest && !isCompareMode ? (
          /* STATE: PM Selected - Show name pill with action buttons below */
          <motion.div
            key="pm-selected"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex flex-col items-center gap-2"
          >
            {/* Selected PM Pill */}
            <div className="flex items-stretch rounded-full overflow-hidden border border-warm-border/50 bg-warm-surface/95 backdrop-blur-md shadow-lg">
              <div className="px-4 py-2.5 flex items-center gap-2">
                <span 
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getColorForCluster(getGuestCluster(selectedGuest)) || '#f97316' }}
                />
                <span className="text-sm font-medium text-text-primary truncate max-w-48">
                  {selectedGuest === '__USER__' ? 'Beautiful You' : selectedGuest}
                </span>
              </div>
              <button
                onClick={() => setSelectedGuest(null)}
                className="px-3 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-warm-surface-hover/50 transition-colors border-l border-warm-border/30"
                title="Clear selection"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Find Most Different Button */}
              <motion.button
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={handleFindMostDifferent}
                className="px-3 py-1.5 bg-orange-primary/10 hover:bg-orange-primary/20 text-orange-primary rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 border border-orange-primary/20"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Find Most Different
              </motion.button>
              
              {/* Compare with Guest Button */}
              <motion.button
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                onClick={handleEnterCompareMode}
                className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 border border-blue-500/20"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Compare with another Guest
              </motion.button>
              
              {/* Compare with You Button - only show for non-user selections */}
              {selectedGuest !== '__USER__' && (
                <motion.button
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  onClick={handleCompareWithYourself}
                  className="px-3 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 border border-violet-500/20"
                >
                  <span className="text-sm">🪞</span>
                  Compare with You
                </motion.button>
              )}
            </div>
          </motion.div>
        ) : (
          /* STATE: Idle - Search input */
          <motion.div
            key="search-idle"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-80"
          >
            <svg 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsDropdownOpen(true);
              }}
              onClick={() => setIsDropdownOpen(true)}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder="Search for a PM guest..."
              className="w-full bg-warm-surface/95 backdrop-blur-md text-text-primary rounded-full pl-11 pr-4 py-2.5 text-sm border border-warm-border shadow-lg focus:border-orange-primary focus:ring-2 focus:ring-orange-primary/20 focus:outline-none transition-all placeholder:text-text-secondary/60"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dropdown Results */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-warm-surface/95 backdrop-blur-md rounded-xl border border-warm-border shadow-xl overflow-hidden z-50"
            style={{ minWidth: '20rem' }}
          >
            <div className="max-h-80 overflow-y-auto">
              {/* "Find Most Different" option - shown when selecting second PM (compare mode or editing PM2) */}
              {((isCompareMode && selectedGuest) || (editingPM === 'pm2' && pm1)) && (
                <button
                  onClick={() => {
                    // Find most different from the first PM
                    const firstPM = editingPM === 'pm2' ? pm1 : selectedGuest;
                    if (!firstPM) return;
                    const mostDifferent = getMostDifferentGuest(firstPM);
                    if (mostDifferent) {
                      if (editingPM === 'pm2') {
                        setCompareComparison(firstPM, mostDifferent.name);
                        setEditingPM(null);
                      } else {
                        setMostDifferentComparison(mostDifferent.name);
                        setIsCompareMode(false);
                      }
                      setIsDropdownOpen(false);
                      onGuestSelect?.();
                    }
                  }}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors border-b border-warm-border/30 ${
                    highlightedIndex === 0
                      ? 'bg-orange-primary/10'
                      : 'hover:bg-warm-surface-hover'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-primary/20 to-red-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-orange-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-orange-primary">Find Most Different</div>
                    <div className="text-xs text-text-secondary/60 truncate">Auto-select PM furthest from {editingPM === 'pm2' ? pm1 : selectedGuest}</div>
                  </div>
                </button>
              )}
              
              {/* Guest list */}
              {filteredGuests.length > 0 ? (
                filteredGuests.map((guest, index) => {
                  const showFindMostDifferent = (isCompareMode && selectedGuest) || (editingPM === 'pm2' && pm1);
                  const adjustedIndex = showFindMostDifferent ? index + 1 : index;
                  const cluster = getGuestCluster(guest);
                  return (
                    <button
                      key={guest}
                      onClick={() => handleSelectPM(guest)}
                      onMouseEnter={() => setHighlightedIndex(adjustedIndex)}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        adjustedIndex === highlightedIndex
                          ? 'bg-orange-primary/10'
                          : 'hover:bg-warm-surface-hover'
                      }`}
                    >
                      {cluster !== undefined && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getColorForCluster(cluster) }}
                        />
                      )}
                      <span className="text-sm text-text-primary truncate flex-1">{guest === '__USER__' ? 'You' : guest}</span>
                      {cluster !== undefined && (
                        <span className="text-xs text-text-secondary">
                          {getClusterName(cluster)}
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="p-4 text-center">
                  <p className="text-sm text-text-secondary">No guests found</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// "Wat Dis?" explanatory modal with tiered complexity
type WatDisTab = 'gist' | 'pm' | 'techie' | 'stats';

const WAT_DIS_CONTENT: Record<WatDisTab, { title: string; emoji: string; content: JSX.Element }> = {
  gist: {
    title: 'The Gist',
    emoji: '✨',
    content: (
      <div className="space-y-4 text-text-primary">
        <p className="text-lg leading-relaxed">
          This is a <span className="text-orange-primary font-semibold">personality map</span> for product managers.
        </p>
        <p className="leading-relaxed text-text-secondary">
          We analyzed 297 conversations from Lenny's Podcast and discovered hidden patterns in how different PMs think. 
          Each dot represents a real person—positioned not by their title or company, but by the <em>ideas they expressed</em> in their interview.
        </p>
        <p className="leading-relaxed text-text-secondary">
          The positioning is entirely data-driven. No human sat down and decided "this PM is more technical" or "that PM is more growth-oriented." 
          The patterns <strong>emerged from the conversations themselves</strong>.
        </p>
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-2.5">
          <p className="flex items-start gap-2"><span className="mt-0.5">📍</span> <span>People positioned close together expressed similar ideas and priorities</span></p>
          <p className="flex items-start gap-2"><span className="mt-0.5">🎨</span> <span>Colors represent natural groupings the algorithm discovered (not pre-defined categories)</span></p>
          <p className="flex items-start gap-2"><span className="mt-0.5">🧭</span> <span>Take the quiz to see where <em>your</em> thinking style lands on this map</span></p>
        </div>
      </div>
    ),
  },
  pm: {
    title: 'For the PM',
    emoji: '📊',
    content: (
      <div className="space-y-4 text-text-primary">
        <p className="text-lg leading-relaxed">
          Think of this as a <span className="text-orange-primary font-semibold">landscape of PM philosophies</span>—revealed by the data, not imposed by labels.
        </p>
        
        <div className="bg-orange-primary/10 border border-orange-primary/30 rounded-lg p-4">
          <p className="font-medium text-orange-primary mb-2">🔑 Key insight: This is objective, not subjective</p>
          <p className="text-text-secondary text-sm leading-relaxed">
            No one manually "graded" these PMs or decided who's more data-driven vs. intuition-led. 
            The axes and positions <strong>emerge mathematically from what guests actually said</strong> in their interviews. 
            When you see someone scoring high on "Platform Thinking," it's because they used language and concepts that 
            statistically cluster with platform-oriented discussions—not because someone labeled them that way.
          </p>
        </div>

        <p className="leading-relaxed text-text-secondary">
          Instead of categorizing PMs by company or title (which tells you little), this map positions them by 
          <em> how they approach product work</em>—their mental models, priorities, and beliefs about what makes great products.
        </p>
        
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-3">
          <p className="font-medium">What you can do here:</p>
          <ul className="space-y-2.5 text-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-orange-primary font-bold">→</span>
              <span><strong>Explore profiles</strong> — Click any PM to see their 20-dimension breakdown. What makes their approach distinctive? Which dimensions define them?</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-primary font-bold">→</span>
              <span><strong>Compare thinking styles</strong> — Select any two PMs and see exactly where they differ. The "most different" feature finds the PM with the most contrasting worldview.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-primary font-bold">→</span>
              <span><strong>Find your tribe</strong> — Take the quiz to place yourself on the map. See which guests think like you—and who represents your "opposite."</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-primary font-bold">→</span>
              <span><strong>Discover archetypes</strong> — The 10 colored clusters reveal emergent PM archetypes: Growth Operators, Technical Architects, Consumer Psychologists, and more.</span>
            </li>
          </ul>
        </div>
        
        <p className="text-sm text-text-muted">
          The 20 dimensions capture themes like: data-driven vs. intuition-led, platform vs. feature thinking, 
          B2B vs. consumer mindset, founder mentality vs. operational excellence, and more.
        </p>
      </div>
    ),
  },
  techie: {
    title: 'For the Techies',
    emoji: '⚙️',
    content: (
      <div className="space-y-4 text-text-primary">
        <p className="text-lg leading-relaxed">
          The interesting part isn't the React app—it's <span className="text-orange-primary font-semibold">how we turned conversations into coordinates</span>.
        </p>
        
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-3">
          <p className="font-medium">The core problem:</p>
          <p className="text-text-secondary text-sm leading-relaxed">
            How do you convert 297 hour-long podcast conversations into a meaningful spatial representation 
            where "closeness" actually means "thinks similarly"? 
          </p>
        </div>

        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-3">
          <p className="font-medium">The pipeline:</p>
          <ol className="space-y-2 text-text-secondary text-sm list-decimal ml-4">
            <li><strong>Entity extraction</strong> — GraphRAG pulls out concepts, people, and relationships from each transcript (not just keywords—semantic understanding)</li>
            <li><strong>Vectorization</strong> — TF-IDF weights these entities per guest, producing 604-dimensional sparse vectors</li>
            <li><strong>Dimensionality reduction</strong> — PCA compresses to 20 dimensions while preserving ~26% of variance</li>
            <li><strong>Rotation</strong> — Varimax rotation makes axes interpretable (each axis loads heavily on semantically related concepts)</li>
          </ol>
        </div>
        
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-3">
          <p className="font-medium">Interesting implementation details:</p>
          <ul className="space-y-2 text-text-secondary text-sm">
            <li>• <strong>Distance is computed in full 20D</strong>, not just the 3 visible axes. The 3D view is a projection—similarity scores use all dimensions.</li>
            <li>• <strong>Hyperslice controls</strong> let you "slice" through the 20D space by fixing dimensions at specific values, revealing hidden clusters.</li>
            <li>• <strong>Quiz embedding</strong> maps your responses to the same 20D space using weighted dimension mappings derived from question semantics.</li>
            <li>• <strong>Percentile rankings</strong> compare any pairwise distance against all ~44K guest pairs to give relative similarity ("more different than 87% of PM pairs").</li>
          </ul>
        </div>
        
        <p className="text-sm text-text-muted italic">
          Stack: React + TypeScript + Three.js (via R3F) + Zustand. Static JSON data, pre-computed coordinates. Vercel for hosting.
        </p>
      </div>
    ),
  },
  stats: {
    title: 'For Statisticians & Linguists',
    emoji: '📐',
    content: (
      <div className="space-y-4 text-text-primary">
        <p className="text-lg leading-relaxed">
          <span className="text-orange-primary font-semibold">GraphRAG → TF-IDF → PCA → Varimax</span> — a pipeline for extracting latent semantic dimensions from unstructured text.
        </p>
        
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-3 text-sm">
          <p className="font-medium">Methodology in detail:</p>
          <ol className="space-y-3 text-text-secondary list-decimal ml-4">
            <li>
              <strong>Keyword/Entity Extraction:</strong> Microsoft's GraphRAG extracts entities and relationships from 297 podcast transcripts. 
              This goes beyond bag-of-words—it captures semantic relationships and disambiguates entities.
            </li>
            <li>
              <strong>Document-Term Matrix:</strong> TF-IDF weighting on extracted entities produces 604-dimensional sparse vectors per guest. 
              IDF downweights common terms (e.g., "product," "user") while emphasizing distinctive vocabulary.
            </li>
            <li>
              <strong>PCA:</strong> Principal Component Analysis reduces to 20 dimensions, retaining ~26% of total variance. 
              Components selected via scree plot elbow + interpretability of loadings.
            </li>
            <li>
              <strong>Varimax Rotation:</strong> Orthogonal rotation maximizes variance of squared loadings, producing axes that load heavily 
              on semantically coherent term clusters (e.g., Dim 3 loads on "experiment," "A/B test," "metrics").
            </li>
            <li>
              <strong>K-means Clustering:</strong> k=10 clusters in the full 20D space, validated via silhouette scores and manual inspection 
              of cluster exemplars.
            </li>
          </ol>
        </div>
        
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-2 text-sm">
          <p className="font-medium">Key statistics:</p>
          <ul className="space-y-1.5 text-text-secondary">
            <li>• <strong>N = 297</strong> guests (podcast episodes through late 2024)</li>
            <li>• <strong>604 unique entities</strong> extracted across corpus</li>
            <li>• <strong>20 principal components</strong> retained (scree plot + interpretability criterion)</li>
            <li>• <strong>~26% cumulative variance explained</strong> (reasonable for high-dimensional text data)</li>
            <li>• <strong>Euclidean distance</strong> in full 20D space for similarity (not cosine—coordinates are already centered/scaled)</li>
            <li>• <strong>~44,000 pairwise distances</strong> computed for percentile ranking baseline</li>
          </ul>
        </div>
        
        <div className="bg-warm-surface/50 rounded-lg p-4 space-y-2 text-sm">
          <p className="font-medium">Dimension labeling approach:</p>
          <p className="text-text-secondary leading-relaxed">
            Each axis was labeled by examining: (1) top-loading keywords, (2) guests at extreme scores on that dimension, 
            (3) LLM-generated interpretations of the loading patterns. Labels were validated by checking that extreme-scoring 
            guests matched intuitive expectations (e.g., "Experimentation-heavy" dimension should have known A/B testing advocates at one end).
          </p>
        </div>
        
        <p className="text-sm text-text-muted italic">
          Limitations: PCA assumes linear relationships; 26% variance means substantial information loss; 
          entity extraction quality depends on GraphRAG's performance on conversational text.
        </p>
      </div>
    ),
  },
};

function WatDisModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<WatDisTab>('gist');
  
  if (!isOpen) return null;
  
  const tabs: { id: WatDisTab; label: string; emoji: string }[] = [
    { id: 'gist', label: 'The Gist', emoji: '✨' },
    { id: 'pm', label: 'For the PM', emoji: '📊' },
    { id: 'techie', label: 'For Techies', emoji: '⚙️' },
    { id: 'stats', label: 'Stats & Linguistics', emoji: '📐' },
  ];
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="relative bg-warm-surface border border-warm-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-warm-border">
              <h2 className="text-xl font-semibold text-text-primary flex items-center gap-2">
                <span className="text-2xl">🤔</span>
                Wat Dis?
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-warm-surface-hover rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b border-warm-border bg-warm-surface/50">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-orange-primary' 
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <span className="flex items-center justify-center gap-1.5">
                    <span>{tab.emoji}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </span>
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="watdis-tab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-primary"
                    />
                  )}
                </button>
              ))}
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {WAT_DIS_CONTENT[activeTab].content}
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-warm-border bg-warm-surface/50 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-orange-primary hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Got it!
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// First-time interaction hint overlay
function InteractionHint({ isVisible, onDismiss }: { isVisible: boolean; onDismiss: () => void }) {
  useEffect(() => {
    if (!isVisible) return;
    
    // Auto-dismiss after 10 seconds (extended for first-time users)
    const timer = setTimeout(onDismiss, 10000);
    
    // Dismiss on any interaction
    const handleInteraction = () => onDismiss();
    window.addEventListener('mousedown', handleInteraction);
    window.addEventListener('wheel', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleInteraction);
      window.removeEventListener('wheel', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [isVisible, onDismiss]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
    >
      <div className="bg-warm-surface/95 backdrop-blur-md rounded-xl border border-warm-border shadow-xl px-6 py-4">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-text-secondary">
            <span className="text-lg">🖱️</span>
            <span>Drag to rotate</span>
          </div>
          <div className="w-px h-4 bg-warm-border" />
          <div className="flex items-center gap-2 text-text-secondary">
            <span className="text-lg">🔍</span>
            <span>Scroll to zoom</span>
          </div>
          <div className="w-px h-4 bg-warm-border" />
          <div className="flex items-center gap-2 text-text-secondary">
            <span className="text-lg">👆</span>
            <span>Click dots to explore</span>
          </div>
        </div>
        <p className="text-xs text-text-muted text-center mt-2">
          Each dot is a PM guest from Lenny's Podcast
        </p>
      </div>
    </motion.div>
  );
}

function App() {
  const { loadData, loading, error, pcaData, setSelectedGuest, getDimensionName, resetView, selectedGuest, sizeMode, setSizeMode, setQuizActive } = usePCAStore();
  const [leftCollapsed, setLeftCollapsed] = useState(false); // Dimensions panel - open by default
  const [rightCollapsed, setRightCollapsed] = useState(true); // Profile panel - collapsed by default
  
  const [showLanding, setShowLanding] = useState(true); // Always start with landing page
  const [showInteractionHint, setShowInteractionHint] = useState(false); // Show hint after landing
  const [showWatDis, setShowWatDis] = useState(false); // "Wat Dis?" info modal
  
  // Collapsible sections in left panel
  const [clustersExpanded, setClustersExpanded] = useState(false);
  const [hypersliceExpanded, setHypersliceExpanded] = useState(false);
  
  // Track previous guest to only auto-open on NEW selections
  const prevSelectedGuestRef = useRef<string | null>(null);
  
  // Open profile panel when a NEW guest is selected (not when user manually collapses)
  useEffect(() => {
    if (selectedGuest && selectedGuest !== prevSelectedGuestRef.current) {
      setRightCollapsed(false);
    }
    prevSelectedGuestRef.current = selectedGuest;
  }, [selectedGuest]);

  useEffect(() => {
    loadData('keywords');  // Load relationship keywords data by default
  }, [loadData]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Escape: Clear selection
    if (event.key === 'Escape') {
      event.preventDefault();
      setSelectedGuest(null);
    }
  }, [setSelectedGuest]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <ErrorBoundary>
      {/* Main Application - always rendered, behind landing page */}
      <div className="w-screen h-screen overflow-hidden bg-warm-bg relative">
            {/* Decorative background elements */}
            <DecorativeElements />
            
            {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-0 left-0 right-0 z-50 h-24 bg-warm-surface/80 backdrop-blur-md border-b border-warm-border shadow-sm"
        >
          <div className="h-full flex items-center justify-center relative">
            {/* Left side - Logo */}
            <div className="absolute left-8 flex items-center">
              <img 
                src={fireplaceLogo} 
                alt="Lenny's Podcast Fireplace Logo" 
                className="w-14 h-14 object-contain"
                onError={(e) => {
                  console.error('Failed to load logo:', fireplaceLogo);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>

            {/* Center - Main Title */}
            <h1 className="title-script text-5xl text-center leading-tight">
              Latent Lenny
            </h1>

            {/* Right side - Stats and Home/About button */}
            <div className="absolute right-8 flex items-center gap-4">
              {pcaData && (
                <span className="text-sm text-text-secondary/60">
                  {pcaData.guestCoordinates.length} guests · {pcaData.guestCoordinates[0]?.coordinates.length || 0} dimensions
                </span>
              )}
              {/* Find Your Place button - opens quiz */}
              <button
                onClick={() => setQuizActive(true)}
                className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-orange-primary border border-warm-border/50 hover:border-orange-primary/30 rounded-lg transition-colors flex items-center gap-1.5"
                title="Find Your Place"
              >
                <span>🧭</span>
                <span>Find Your Place</span>
              </button>
              {/* Home/About button - opens landing page */}
              {/* Stacked CTAs: Home and Wat Dis */}
              <div className="flex flex-col gap-1.5">
                {/* Home button - opens landing page */}
                <button
                  onClick={() => setShowLanding(true)}
                  className="px-3 py-1.5 bg-warm-surface/60 hover:bg-warm-surface-hover/80 border border-warm-border rounded-lg text-xs font-medium text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1.5"
                  title="Back to start"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Home
                </button>
                
                {/* Wat Dis? button - opens info modal */}
                <button
                  onClick={() => setShowWatDis(true)}
                  className="px-3 py-1.5 bg-orange-primary/10 hover:bg-orange-primary/20 border border-orange-primary/30 rounded-lg text-xs font-medium text-orange-primary hover:text-orange-600 transition-colors flex items-center gap-1.5"
                  title="Learn how this works"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Wat Dis?
                </button>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Main Content */}
        <div className="relative h-full pt-24">
          {/* 3D Scene - Full width, behind panels */}
          <main className="absolute inset-0 w-full h-full">
            {/* Reset View Button - Top left, shifts right when dimensions panel is open */}
            {pcaData && !loading && (
              <button
                onClick={() => resetView()}
                className={`absolute top-28 z-30 px-3 py-1.5 bg-warm-surface/90 backdrop-blur-md border border-warm-border rounded-lg text-xs font-medium text-text-primary hover:bg-warm-surface-hover transition-all duration-300 shadow-md flex items-center gap-1.5 ${
                  leftCollapsed ? 'left-4' : 'left-[21rem]'
                }`}
                title="Reset axes and camera view"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset View
              </button>
            )}
            
            {/* Guest Search - Floating at top center */}
            {pcaData && !loading && (
              <div className="absolute top-28 left-1/2 -translate-x-1/2 z-30">
                <GuestSearch onGuestSelect={() => setRightCollapsed(false)} />
              </div>
            )}

            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 flex items-center justify-center z-10 bg-warm-bg/90"
              >
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-orange-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-text-primary">Loading PCA data...</p>
                </div>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 flex items-center justify-center z-10 bg-warm-bg/90"
              >
                <div className="bg-red-100 border border-red-300 rounded-lg p-6 text-center shadow-lg">
                  <p className="text-red-600 font-semibold mb-2">Error loading data</p>
                  <p className="text-text-secondary text-sm">{error}</p>
                </div>
              </motion.div>
            )}

            {!loading && !error && <Scene3D />}
            
            {/* First-time interaction hint */}
            <AnimatePresence>
              {showInteractionHint && (
                <InteractionHint 
                  isVisible={showInteractionHint} 
                  onDismiss={() => setShowInteractionHint(false)} 
                />
              )}
            </AnimatePresence>
          </main>

          {/* Left Sidebar - Dimensions - Overlay */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ 
              opacity: 1, 
              x: leftCollapsed ? -352 : 0,
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed left-0 top-24 bottom-0 z-40 w-88 overflow-hidden pointer-events-none"
            style={{ pointerEvents: leftCollapsed ? 'none' : 'auto' }}
          >
            <div className="w-88 py-4 h-full px-4">
              {/* Combined Dimensions Panel */}
              <div className="bg-warm-surface/95 backdrop-blur-md rounded-lg border border-warm-border shadow-xl h-full flex flex-col pointer-events-auto">
                {/* Header with collapse button */}
                <div className="flex items-center justify-between p-4 pb-3">
                  <h3 className="text-lg font-semibold text-text-primary">Dimensions</h3>
                  <button
                    onClick={() => setLeftCollapsed(true)}
                    className="p-1.5 rounded-md hover:bg-warm-surface-hover transition-colors"
                    title="Collapse panel"
                  >
                    <SidePanelIcon side="left" className="w-4 h-4 text-text-secondary" />
                  </button>
                </div>

                {/* Size Mode Toggle - Moved higher */}
                <div className="px-4 pb-3 border-b border-warm-border/50">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-secondary font-medium">Size by:</span>
                    <div className="flex rounded-md overflow-hidden border border-warm-border">
                      <button
                        onClick={() => setSizeMode('fixed')}
                        className={`px-2 py-1 text-xs transition-colors ${
                          sizeMode === 'fixed'
                            ? 'bg-orange-primary text-white'
                            : 'bg-warm-surface hover:bg-warm-surface-hover text-text-secondary'
                        }`}
                      >
                        Fixed
                      </button>
                      <button
                        onClick={() => setSizeMode('popularity')}
                        className={`px-2 py-1 text-xs transition-colors ${
                          sizeMode === 'popularity'
                            ? 'bg-orange-primary text-white'
                            : 'bg-warm-surface hover:bg-warm-surface-hover text-text-secondary'
                        }`}
                      >
                        Popularity
                      </button>
                    </div>
                  </div>
                </div>

                {/* 3D Axes Info */}
                <div className="px-4 pb-3">
                  <div className="text-xs text-text-secondary mb-2 font-medium">3D Axes</div>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-400 border border-rose-200 text-[9px] font-bold flex-shrink-0">1</span>
                      <span className="text-text-secondary leading-relaxed">{getDimensionName(1)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-400 border border-emerald-200 text-[9px] font-bold flex-shrink-0">2</span>
                      <span className="text-text-secondary leading-relaxed">{getDimensionName(2)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-400 border border-sky-200 text-[9px] font-bold flex-shrink-0">3</span>
                      <span className="text-text-secondary leading-relaxed">{getDimensionName(3)}</span>
                    </div>
                  </div>
                </div>

                {/* Collapsible: Clusters */}
                <div className="border-t border-warm-border/50">
                  <button
                    onClick={() => setClustersExpanded(!clustersExpanded)}
                    className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-warm-surface-hover/30 transition-colors"
                  >
                    <span className="text-xs text-text-secondary font-medium">Clusters</span>
                    <svg 
                      className={`w-3.5 h-3.5 text-text-secondary/50 transition-transform ${clustersExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {clustersExpanded && (
                    <div className="px-4 pb-3">
                      <ClusterLegend />
                    </div>
                  )}
                </div>

                {/* Collapsible: Hyperslice */}
                <div className="border-t border-warm-border/50 flex-1 flex flex-col min-h-0">
                  <button
                    onClick={() => setHypersliceExpanded(!hypersliceExpanded)}
                    className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-warm-surface-hover/30 transition-colors flex-shrink-0"
                  >
                    <span className="text-xs text-text-secondary font-medium">Hyperslice</span>
                    <svg 
                      className={`w-3.5 h-3.5 text-text-secondary/50 transition-transform ${hypersliceExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {hypersliceExpanded && (
                    <div className="flex-1 overflow-y-auto">
                      <HyperslicerPanel />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.aside>

          {/* Collapsed left tab */}
          <AnimatePresence>
            {leftCollapsed && (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onClick={() => setLeftCollapsed(false)}
                className="fixed left-0 top-1/2 -translate-y-1/2 z-30 bg-warm-surface/90 backdrop-blur-md border border-l-0 border-warm-border rounded-r-lg p-2 shadow-md hover:bg-warm-surface-hover transition-colors flex flex-col items-center gap-2"
              >
                <SidePanelIcon side="left" className="w-4 h-4 text-text-secondary" />
                <span className="text-xs text-text-secondary font-medium" style={{ writingMode: 'vertical-rl' }}>
                  Dimensions
                </span>
              </motion.button>
            )}
          </AnimatePresence>

          {/* Right Sidebar - Profile Panel - Overlay */}
          <motion.aside
            initial={{ opacity: 0, x: 20 }}
            animate={{ 
              opacity: 1, 
              x: rightCollapsed ? 384 : 0,
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed right-0 top-24 bottom-0 z-40 w-96 overflow-hidden pointer-events-none"
            style={{ pointerEvents: rightCollapsed ? 'none' : 'auto' }}
          >
            <div className="w-96 py-4 h-full px-4">
              <div className="bg-warm-surface/95 backdrop-blur-md rounded-lg border border-warm-border shadow-xl h-full pointer-events-auto overflow-hidden flex flex-col">
                <ProfilePanel onCollapse={() => setRightCollapsed(true)} />
              </div>
            </div>
          </motion.aside>

          {/* Collapsed right tab */}
          <AnimatePresence>
            {rightCollapsed && (
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onClick={() => setRightCollapsed(false)}
                className="fixed right-0 top-1/2 -translate-y-1/2 z-30 bg-warm-surface/90 backdrop-blur-md border border-r-0 border-warm-border rounded-l-lg p-2 shadow-md hover:bg-warm-surface-hover transition-colors flex flex-col items-center gap-2"
              >
                <SidePanelIcon side="right" className="w-4 h-4 text-text-secondary" />
                <span className="text-xs text-text-secondary font-medium" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                  Profile
                </span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Landing Page - overlays the space, reveals it when curtains open */}
      {showLanding && (
        <LandingPage 
          isVisible={showLanding}
          onReveal={() => {
            setShowLanding(false);
            setShowInteractionHint(true); // Show hint after landing
            // Reset panel states for fresh experience
            setLeftCollapsed(false); // Dimensions open
            setRightCollapsed(true); // Profile collapsed
          }} 
        />
      )}

      {/* Wat Dis? Info Modal */}
      <WatDisModal isOpen={showWatDis} onClose={() => setShowWatDis(false)} />

      {/* Quiz Modal */}
      <QuizModal />
    </ErrorBoundary>
  );
}

export default App;
